/**
 * Implication Repository - Platform Persistence layer.
 *
 * Public interface for StructuredImplicationAnalysis artifact persistence.
 * All reads and writes go through this module. No caller touches Supabase
 * directly for implication analysis artifacts.
 *
 * Write path:
 *   writeImplicationAnalysis()
 *     → supabase.rpc("write_implication_analysis_artifact")  [atomic: version + both INSERTs]
 *     → reads RETURNING (version, created_at)
 *     → assembles ArtifactEnvelope from authoritative database values
 *     → constructArtifact(envelope, content) → StructuredImplicationAnalysis
 *     → retries up to MAX_VERSION_RETRIES times on UNIQUE constraint violation
 *
 * Read path:
 *   readImplicationAnalysisByVersion / readLatestImplicationAnalysis / readImplicationLineage
 *     → joined select on pi_artifact_envelopes + implication_analysis_contents
 *     → rowToImplicationAnalysis() reassembles typed StructuredImplicationAnalysis
 *
 * displayName parameter:
 *   ImplicationContent carries no display label - it is engine output only.
 *   The caller (orchestration layer) supplies displayName as explicit artifact
 *   metadata. It is stored in the content table for operational purposes but
 *   is not part of the ImplicationContent type contract.
 *
 * ArtifactRef fields:
 *   solutionConceptRef and discoveryReportRef are denormalized into six scalar
 *   columns in implication_analysis_contents. On read they are reassembled into
 *   ArtifactRef objects. No FK constraint exists - refs record domain provenance.
 *
 * Responsibility boundary:
 *   This module owns: RPC invocation, retry logic, RETURNING assembly,
 *   constructArtifact() call, error translation, public repository interface.
 *   Database layer owns: version arithmetic, timestamp generation, atomicity.
 *   Governance layer owns: artifact identity semantics, type contracts.
 */

import { supabase } from "@/integrations/supabase/client";
import type { ArtifactEnvelope, ArtifactRef } from "@/types/platformArtifactTypes";
import type { ImplicationContent, StructuredImplicationAnalysis } from "@/intelligence/strategicPITypes";
import { constructArtifact } from "@/platform/artifactConstructor";
import { logger } from "@/system/logger";
import { toJson } from "@/integrations/supabase/json";

const MAX_VERSION_RETRIES = 3;
const UNIQUE_VIOLATION_CODE = "23505";

// ─── Private helpers ──────────────────────────────────────────────────────────

type ImplicationEnvelopeWithContent = {
  version:    number;
  created_at: string;
  implication_analysis_contents: {
    sc_project_id:               string;
    sc_artifact_type:            string;
    sc_version:                  number;
    dr_project_id:               string;
    dr_artifact_type:            string;
    dr_version:                  number;
    architectural_implications:  unknown;
    required_capabilities:       unknown;
    optional_capabilities:       unknown;
    identified_risks:            unknown;
    external_dependencies:       unknown;
    detected_signals:            unknown;
    analysis_rationale:          string;
    confidence:                  number;
  } | null;
};

function rowToImplicationAnalysis(
  projectId: string,
  row: ImplicationEnvelopeWithContent,
): StructuredImplicationAnalysis | null {
  const c = row.implication_analysis_contents;
  if (!c) return null;

  const envelope: ArtifactEnvelope = {
    projectId,
    artifactType: "implication_analysis",
    version:      row.version,
    createdAt:    row.created_at,
  };

  const solutionConceptRef: ArtifactRef = {
    projectId:    c.sc_project_id,
    artifactType: c.sc_artifact_type as ArtifactRef["artifactType"],
    version:      c.sc_version,
  };

  const discoveryReportRef: ArtifactRef = {
    projectId:    c.dr_project_id,
    artifactType: c.dr_artifact_type as ArtifactRef["artifactType"],
    version:      c.dr_version,
  };

  const content: ImplicationContent = {
    solutionConceptRef,
    discoveryReportRef,
    architecturalImplications: c.architectural_implications as ImplicationContent["architecturalImplications"],
    requiredCapabilities:      c.required_capabilities      as ImplicationContent["requiredCapabilities"],
    optionalCapabilities:      c.optional_capabilities      as ImplicationContent["optionalCapabilities"],
    identifiedRisks:           c.identified_risks           as ImplicationContent["identifiedRisks"],
    externalDependencies:      c.external_dependencies      as ImplicationContent["externalDependencies"],
    detectedSignals:           c.detected_signals           as ImplicationContent["detectedSignals"],
    analysisRationale:         c.analysis_rationale,
    confidence:                c.confidence,
  };

  return constructArtifact(envelope, content);
}

// ─── Write ────────────────────────────────────────────────────────────────────

/**
 * Persist a new StructuredImplicationAnalysis artifact version atomically.
 *
 * The RPC assigns the next contiguous version via MAX+1 inside the same
 * transaction that inserts both rows. On UNIQUE constraint violation (concurrent
 * writers landing on the same version), this function retries up to
 * MAX_VERSION_RETRIES times before throwing.
 *
 * displayName is stored in the content table for operational labeling.
 * It is not part of ImplicationContent and is not accessible on the returned
 * StructuredImplicationAnalysis - it is write-path metadata only.
 */
export async function writeImplicationAnalysis(
  projectId:   string,
  displayName: string,
  content:     ImplicationContent,
): Promise<StructuredImplicationAnalysis> {
  let attempt = 0;

  while (attempt < MAX_VERSION_RETRIES) {
    const { data, error } = await supabase.rpc("write_implication_analysis_artifact", {
      p_project_id:                 projectId,
      p_display_name:               displayName,
      p_sc_project_id:              content.solutionConceptRef.projectId,
      p_sc_artifact_type:           content.solutionConceptRef.artifactType,
      p_sc_version:                 content.solutionConceptRef.version,
      p_dr_project_id:              content.discoveryReportRef.projectId,
      p_dr_artifact_type:           content.discoveryReportRef.artifactType,
      p_dr_version:                 content.discoveryReportRef.version,
      p_architectural_implications: toJson(content.architecturalImplications),
      p_required_capabilities:      toJson(content.requiredCapabilities),
      p_optional_capabilities:      toJson(content.optionalCapabilities),
      p_identified_risks:           toJson(content.identifiedRisks),
      p_external_dependencies:      toJson(content.externalDependencies),
      p_detected_signals:           toJson(content.detectedSignals),
      p_analysis_rationale:         content.analysisRationale,
      p_confidence:                 content.confidence,
    });

    if (!error) {
      const row = (data as Array<{ version: number; created_at: string }>)[0];
      const envelope: ArtifactEnvelope = {
        projectId,
        artifactType: "implication_analysis",
        version:      row.version,
        createdAt:    row.created_at,
      };
      return constructArtifact(envelope, content);
    }

    if ((error as { code?: string }).code === UNIQUE_VIOLATION_CODE) {
      attempt++;
      logger.warn("ImplicationAnalysis version conflict, retrying", { projectId, attempt });
      continue;
    }

    logger.error("writeImplicationAnalysis RPC failed", { projectId, error });
    throw new Error(`writeImplicationAnalysis failed: ${error.message}`);
  }

  throw new Error(
    `writeImplicationAnalysis exhausted ${MAX_VERSION_RETRIES} retries for project ${projectId}`,
  );
}

// ─── Read ─────────────────────────────────────────────────────────────────────

/**
 * Read a specific StructuredImplicationAnalysis version. Returns null if the
 * version does not exist for this project.
 */
export async function readImplicationAnalysisByVersion(
  projectId: string,
  version:   number,
): Promise<StructuredImplicationAnalysis | null> {
  const { data, error } = await supabase
    .from("pi_artifact_envelopes")
    .select("version, created_at, implication_analysis_contents (*)")
    .eq("project_id", projectId)
    .eq("artifact_type", "implication_analysis")
    .eq("version", version)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null;
    logger.error("readImplicationAnalysisByVersion failed", { projectId, version, error });
    throw new Error(`readImplicationAnalysisByVersion failed: ${error.message}`);
  }

  return rowToImplicationAnalysis(projectId, data as unknown as ImplicationEnvelopeWithContent);
}

/**
 * Read the latest StructuredImplicationAnalysis version for a project. Returns
 * null if no implication analyses exist for the project.
 */
export async function readLatestImplicationAnalysis(
  projectId: string,
): Promise<StructuredImplicationAnalysis | null> {
  const { data, error } = await supabase
    .from("pi_artifact_envelopes")
    .select("version, created_at, implication_analysis_contents (*)")
    .eq("project_id", projectId)
    .eq("artifact_type", "implication_analysis")
    .order("version", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    logger.error("readLatestImplicationAnalysis failed", { projectId, error });
    throw new Error(`readLatestImplicationAnalysis failed: ${error.message}`);
  }

  if (!data) return null;
  return rowToImplicationAnalysis(projectId, data as unknown as ImplicationEnvelopeWithContent);
}

/**
 * Read all StructuredImplicationAnalysis versions for a project in ascending
 * version order. Returns an empty array if no analyses exist for the project.
 */
export async function readImplicationLineage(
  projectId: string,
): Promise<StructuredImplicationAnalysis[]> {
  const { data, error } = await supabase
    .from("pi_artifact_envelopes")
    .select("version, created_at, implication_analysis_contents (*)")
    .eq("project_id", projectId)
    .eq("artifact_type", "implication_analysis")
    .order("version", { ascending: true });

  if (error) {
    logger.error("readImplicationLineage failed", { projectId, error });
    throw new Error(`readImplicationLineage failed: ${error.message}`);
  }

  return (data as unknown as ImplicationEnvelopeWithContent[])
    .map(row => rowToImplicationAnalysis(projectId, row))
    .filter((d): d is StructuredImplicationAnalysis => d !== null);
}
